// EditStartWorkout.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, Select, MenuItem, Button, Dialog, DialogTitle, DialogContent, TextField, Autocomplete } from '@mui/material';
import { getExercises, getWorkoutProgram, updateWorkoutProgram, updateProgramByWorkoutProgramId } from '../../utils/api';
import ProgramExerciseItem from './ProgramExerciseItem';
import jsPDF from 'jspdf';
import GveretLevinFont from '../../assets/gevlevin-normal.js';
import Loader from '../Loader.js';
const EditStartWorkout = ({ userId }) => {
    const { programId } = useParams();
    const navigate = useNavigate();
    const [exercises, setExercises] = useState([]);
    const [workoutType, setWorkoutType] = useState('');
    const [expertiseLevel, setExpertiseLevel] = useState('');
    const [customProgram, setCustomProgram] = useState({});
    const [timesPerWeek, setTimesPerWeek] = useState(null);
    const [restDays, setRestDays] = useState('');
    const [generalNotes, setGeneralNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [sets, setSets] = useState('');
    const [reps, setReps] = useState('');
    const [weight, setWeight] = useState('');
    const [day, setDay] = useState('');
    const dayOptions = ['אימון 1:', 'אימון 2:', 'אימון 3:', 'אימון 4:', 'אימון 5:', 'אימון 6:', 'אימון 7:'];

    const exerciseNameMapping = {
        "Cat stretch": "מתיחת חתול",
        "Dumbbell Bicep Curl:": "כפיפת מרפקים עם משקולת",
        "Dumbbell Hammer Curl": "כפיפת מרפקים פטיש עם משקולת",
        "Prone abdominal stretch": "מתיחת בטן בשכיבה על הבטן",
        "Chest and biceps stretch": "מתיחת חזה ושרירי יד קדמיים",
        "Standing quadriceps stretch": "מתיחת ארבע ראשי בעמידה", // Changed from "רגל קדמית" to be more specific
        "Lying glute stretch": "מתיחת ישבן בשכיבה",
        "Bent-over barbell row": "חתירה עם מוט בעמידה כפופה",
        "Twisting hip extension": "הארכת ירך עם סיבוב",
        "Smith machine bent-knee good morning": "בוקר טוב עם מכונת סמית' וברכיים כפופות",
        "Machine back extension": "הארכת גב במכונה",
        "Good morning": "בוקר טוב",
        "Bird dog": "ציפור-כלב",
        "Superman": "סופרמן",
        "Assisted Chin Tuck": "טאק סנטר בסיוע",
        "Weighted Lying Neck Flexion (with head harness)": "כפיפת צוואר משוקללת בשכיבה (עם רתמה)",
        "Weighted Lying Neck Extension (with head harness)": "הארכת צוואר משוקללת בשכיבה (עם רתמה)",
        "Stability ball wall squat": "סקווט קיר עם כדור יציבות",
        "Bodyweight squat": "סקווט עם משקל גוף",
        "Dumbbell deadlift": "דדליפט עם משקולות יד",
        "Zercher squat": "סקווט זרשר",
        "Barbell front box squat": "סקווט קדמי לקופסה עם מוט", // Adjusted "על קופסה" to "לקופסה" for clarity
        "Barbell front squat": "סקווט קדמי עם מוט",
        "Barbell Bulgarian split squat": "סקווט מפוצל בולגרי עם מוט",
        "Dumbbell one-leg split squat": "סקווט מפוצל חד-רגלי עם משקולת",
        "Barbell side lunge": "לאנג' צידי עם מוט",
        "Cable hip abduction": "הרחקת ירך עם כבל",
        "Raised knee walking": "הליכה עם הרמת ברכיים",
        "Lateral leg raises": "הרמות רגל צידיות",
        "Standing Hip Abduction": "הרחקת ירך בעמידה",
        "Trap bar deadlift": "דדליפט עם מוט טרפז",
        "Dumbbell step-up": "עליית מדרגה עם משקולת",
        "Barbell one-leg hip thrust": "דחיפת ירך חד-רגלית עם מוט",
        "Barbell hip thrust": "דחיפת ירך עם מוט",
        "Weighted one-leg hip thrust": "דחיפת ירך חד-רגלית משוקללת",
        "Dirty Dog": "כלב מלוכלך",
        "Side Lying Clam Hips": "צדפה בשכיבה צידית", // Removed "לירכיים" as it’s redundant
        "Lunge": "לאנג'",
        "kneeling one arm pulldown": "משיכה חד-זרועית בברכיים",
        "Dumbbell Bench Squat": "סקווט עם משקולת ליד ספסל", // Clarified as a squat near a bench
        "Kneeling hip flexor stretch": "מתיחת כופפי ירך בברכיים", // Changed "מפשעה" to specify hip flexors
        "Side Lunge Stretch": "מתיחת לאנג' צידי",
        "Machine seated hip abduction": "הרחקת ירך בישיבה במכונה",
        "Single leg glute bridge": "גשר ישבן חד-רגלי",
        "Reverse Lunge": "לאנג' אחורי",
        "Barbell lunge": "לאנג' עם מוט",
        "Deep Squat": "סקווט עמוק", // Corrected from "סומו עמוק" to distinguish from Sumo Squat
        "bent over hip extension": "הארכת ירך כפופה",
        "Dumbbell lunge": "לאנג' עם משקולת",
        "Dumbbell forward-leaning lunge": "לאנג' עם נטייה קדימה ומשקולת", // Adjusted for better phrasing
        "Barbell deadlift": "דדליפט עם מוט",
        "Stability ball V-pass": "מעבר V עם כדור יציבות",
        "Bent-knee oblique v-up": "V-up אלכסוני עם ברכיים כפופות",
        "Oblique Crunches – On The Floor": "קראנצ'ים אלכסוניים על הרצפה",
        "Dead Bug": "חיפושית מתה",
        "Swiss Ball Rollout": "גלגול כדור שווייצרי",
        "Vertical leg crunch": "קראנץ' רגליים אנכי",
        "Twisting crunch": "קראנץ' מסתובב",
        "Hanging leg raise": "הרמת רגליים בתלייה",
        "Crunch with stability ball leg raise": "קראנץ' עם הרמת רגליים וכדור יציבות",
        "Weighted stability ball side bend": "כיפוף צידי משוקלל עם כדור יציבות",
        "V-up": "V-up",
        "Crunch (hands overhead)": "קראנץ' (ידיים מעל הראש)",
        "Kneeling cable crunch": "קראנץ' כבלים בברכיים",
        "Crunch (arms straight)": "קראנץ' (זרועות ישרות)",
        "Crunch Floor": "קראנץ' על הרצפה", // Simplified from "רצפה"
        "Frog crunch": "קראנץ' צפרדע",
        "Decline sit-up": "סיט-אפ בירידה",
        "Crunch (leg raise)": "קראנץ' (הרמת רגליים)",
        "Bicycle crunch": "קראנץ' אופניים",
        "Pistol Squat to Box": "סקווט פיסטול לקופסה",
        "Alternate Lying Floor Leg Raise": "הרמת רגליים לסירוגין בשכיבה על הרצפה",
        "Stability ball jackknife": "ג'קנייף עם כדור יציבות",
        "Sit-up": "סיט-אפ",
        "Cable one-arm reverse-grip triceps push-down": "דחיפת טריספס חד-זרועית עם אחיזה הפוכה וכבל",
        "Seated dumbbell overhead triceps extension": "הארכת טריספס מעל הראש בישיבה עם משקולת",
        "Seated bent-over two-arm dumbbell kickback": "קיקבק דו-זרועי בישיבה כפופה עם משקולות",
        "Triceps dip": "דיפ טריספס",
        "Reverse Grip Triceps Pushdown": "דחיפת טריספס עם אחיזה הפוכה",
        "Bent-knee bench dip": "דיפ ספסל עם ברכיים כפופות",
        "Triceps rope push-down": "דחיפת טריספס עם חבל",
        "One-arm dumbbell preacher curl": "כפיפת מרפקים פריצ'ר חד-זרועי עם משקולת",
        "Dumbbell one-arm reverse wrist curl": "כפיפת מרפקים שורש כף יד הפוך חד-זרועי עם משקולת",
        "Behind-the-back barbell wrist curl": "כפיפת מרפקים שורש כף יד מאחורי הגב עם מוט",
        "Cable curl": "כפיפת מרפקים עם כבל",
        "Seated barbell wrist curl": "כפיפת מרפקים שורש כף יד בישיבה עם מוט",
        "Dumbbell curl": "כפיפת מרפקים עם משקולת",
        "Dumbbell reverse curl": "כפיפת מרפקים הפוך עם משקולת",
        "Barbell reverse curl": "כפיפת מרפקים הפוך עם מוט",
        "EZ bar reverse curl": "כפיפת מרפקים הפוך עם מוט EZ",
        "Barbell skull crusher": "סקאל קראשר עם מוט",
        "Barbell JM press": "לחיצת JM עם מוט", // Corrected word order
        "Close-grip EZ bar curl": "כפיפת מרפקים צמוד עם מוט EZ",
        "Overhead EZ bar triceps extension": "הארכת טריספס מעל הראש עם מוט EZ",
        "Swiss Ball Lying Triceps Extension": "הארכת טריספס בשכיבה על כדור שווייצרי",
        "EZ bar curl": "כפיפת מרפקים עם מוט EZ",
        "Dumbbell hammer preacher curl": "כפיפת מרפקים פטיש פריצ'ר עם משקולת",
        "Tate press": "לחיצת טייט",
        "Cable triceps kickback": "קיקבק טריספס עם כבל",
        "Close-grip push-up": "שכיבות סמיכה צמודות",
        "Dumbbell kickback": "קיקבק עם משקולת",
        "Dumbbell One-Arm Triceps Extension Instructions": "הארכת טריספס חד-זרועית עם משקולת",
        "Dumbbell Seated Revers grip Concentration Curl": "כפיפת מרפקים ריכוז בישיבה עם אחיזה הפוכה ומשקולת",
        "Dumbbell Kickbacks on Exercise Ball": "קיקבק עם משקולת על כדור אימון",
        "Freedom Band Pushdown": "דחיפה עם רצועת חופש",
        "Glute Bridge on floor": "גשר ישבן על הרצפה",
        "Prone incline wide-grip upright row": "חתירה אנכית רחבה בשכיבה על ספסל משופע",
        "Barbell Clean and Press": "קלין ולחיצה עם מוט", // Adjusted "אנד" to "ו"
        "Barbell shrug": "שרג עם מוט",
        "Bent-over dumbbell row": "חתירה כפופה עם משקולות",
        "One-leg hyperextension": "הארכת גב חד-רגלית",
        "Standing cable rear delt row with rope": "חתירת דלתא אחורית בעמידה עם חבל",
        "wide front grip lat pull downs": "משיכות לאט רחבות עם אחיזה קדמית",
        "two arm dumbbell rows": "חתירה דו-זרועית עם משקולות",
        "Keiser twist (down up)": "טוויסט קייזר (למטה-למעלה)",
        "Freedom Pull Apart": "משיכה חופשית",
        "Capol over rear lateral raise": "הרמה צידית אחורית מעל הראש", // Assuming "Capol" is a typo or brand
        "Hyperextension Bench Exercise": "תרגיל הארכת גב על ספסל",
        "Pull-up": "מתח",
        "Bent-over two-arm dumbbell row": "חתירה כפופה דו-זרועית עם משקולות",
        "Underhand Yates row": "חתירת ייטס עם אחיזה תחתית",
        "Ankle – Dorsal Flexion": "פשיטת קרסול",
        "Machine seated calf raise": "הרמת עגלים בישיבה במכונה",
        "Mountain Climbers": "מטפסי הרים",
        "High bird dog plank": "פלאנק ציפור-כלב גבוה",
        "Band twist (up down)": "טוויסט עם רצועה (למעלה-למטה)",
        "Stability ball decline push-up": "שכיבות סמיכה בירידה עם כדור יציבות",
        "One-leg push-up": "שכיבות סמיכה חד-רגלית",
        "Incline dumbbell fly": "פלי משקולות בשיפוע",
        "Isometric wiper": "וויפר איזומטרי",
        "Front Dumbbell Raise": "הרמת משקולות קדמית",
        "Machine fly": "פלי במכונה",
        "Dumbbell fly": "פלי עם משקולות",
        "Decline dumbbell bench press": "לחיצת חזה בירידה עם משקולות",
        "Dumbbell bench press": "לחיצת חזה עם משקולות",
        "Cable crossovers": "קרוסאובר עם כבלים",
        "high fly": "פלי גבוה",
        "Incline barbell bench press": "לחיצת חזה בשיפוע עם מוט",
        "Decline barbell bench press": "לחיצת חזה בירידה עם מוט",
        "Push up (on knees)": "שכיבות סמיכה (על הברכיים)",
        "Push up (bosu ball)": "שכיבות סמיכה (על כדור בוסו)",
        "Push-ups": "שכיבות סמיכה",
        "Kettlebell swing": "נפת קטלבל",
        "Decline dumbbell fly": "פלי בירידה עם משקולות",
        "Weighted push-up": "שכיבות סמיכה משוקללות",
        "Stability ball push-up": "שכיבות סמיכה עם כדור יציבות",
        "Seated dumbbell overhead press": "לחיצת כתפיים מעל הראש בישיבה עם משקולות",
        "Behind-the-neck barbell overhead press": "לחיצת כתפיים מעל הראש מאחורי הצוואר עם מוט",
        "Smith machine shrug": "שרג במכונת סמית'",
        "Dumbbell w-press": "לחיצת W עם משקולות",
        "Cable front raise": "הרמה קדמית עם כבל",
        "Cable one-arm front raise": "הרמה קדמית חד-זרועית עם כבל",
        "Dumbbell Arnold Press": "לחיצת ארנולד עם משקולות",
        "Weighted Front Raise": "הרמה קדמית משוקללת",
        "Seated alternating dumbbell front raise": "הרמה קדמית לסירוגין בישיבה עם משקולות",
        "Barbell wide-grip upright row": "חתירה אנכית רחבה עם מוט",
        "One-arm dumbbell bent-over lateral raise": "הרמה צידית חד-זרועית כפופה עם משקולת",
        "Alternating dumbbell front raise": "הרמה קדמית לסירוגין עם משקולות",
        "Seated dumbbell front raise": "הרמה קדמית בישיבה עם משקולות",
        "Dumbbell lateral raise": "הרמה צידית עם משקולות",
        "Deltoid Raise": "הרמת דלתואיד",
        "Shoulder Press": "לחיצת כתפיים",
        "Double cable front raise": "הרמה קדמית כפולה עם כבלים",
        "Barbell Upright Row": "חתירה אנכית עם מוט",
        "Lying dumbbell one-arm rear lateral raise": "הרמה צידית אחורית חד-זרועית בשכיבה עם משקולת",
        "Πιέσεις με αλτήρες για ώμους – όρθια στάση": "לחיצת כתפיים עם משקולות בעמידה",
        "Dumbbell one-arm overhead press": "לחיצת כתפיים חד-זרועית מעל הראש עם משקולת",
        "Seated barbell overhead press": "לחיצת כתפיים מעל הראש בישיבה עם מוט",
        "Dumbbell Cuban rotation": "רוטציה קובנית עם משקולת",
        "Εμπρόσθιες άρσεις ώμων με μπάρα": "הרמות קדמיות לכתפיים עם מוט",
        "Lying dumbbell rear lateral raise": "הרמה צידית אחורית בשכיבה עם משקולת",
        "Έξω στροφή ώμου με τον ώμο σε 90 μοίρες απαγωγή": "סיבוב חיצוני של הכתף ב-90 מעלות אבדוקציה",
        "Cable external shoulder rotation": "סיבוב חיצוני של הכתף עם כבל",
        "Dumbbell Scott press": "לחיצת סקוט עם משקולת",
        "Άρσεις τραπεζοειδών με μπάρα": "הרמות טרפז עם מוט",
        "Dumbbell bent-over lateral raise": "הרמה צידית כפופה עם משקולת",
        "Seated bent-over lateral raise": "הרמה צידית כפופה בישיבה",
        "Barbell overhead press": "לחיצת כתפיים מעל הראש עם מוט",
        "Barbell Romanian Deadlift": "דדליפט רומני עם מוט",
        "Kettlebell Goblet Squat": "סקווט גביע עם קטלבל",
        "Standing Calf Raise": "הרמת עגלים בעמידה",
        "Seated Calf Raise": "הרמת עגלים בישיבה",
        "Dumbbell Romanian Deadlift": "דדליפט רומני עם משקולות",
        "Side Plank": "פלאנק צידי",
        "Plank": "פלאנק",
        "Russian Twist": "טוויסט רוסי",
        "Weighted Plank": "פלאנק משוקלל",
        "Cable Woodchopper": "חוטב עצים עם כבל",
        "Leg Press": "לחיצת רגליים",
        "Hack Squat": "סקווט האק",
        "Dumbbell Goblet Squat": "סקווט גביע עם משקולת",
        "Box Jump": "קפיצה לקופסה",
        "Step-Up": "עליית מדרגה",
        "Farmer’s Walk": "הליכת חוואי",
        "Wall Sit": "ישיבה על הקיר",
        "Reverse Hyper": "היפר הפוך",
        "Glute Kickback": "קיקבק ישבן",
        "Donkey Calf Raise": "הרמת עגלים דונקי",
        "Seated Leg Curl": "כפיפת רגליים בישיבה",
        "Lying Leg Curl": "כפיפת רגליים בשכיבה",
        "Nordic Hamstring Curl": "כפיפת רגליים נורדית",
        "Barbell Bench Press": "לחיצת חזה עם מוט",
        "Incline Push-Up": "שכיבות סמיכה בשיפוע",
        "Dumbbell Pullover": "פולאובר עם משקולת",
        "Chest Press Machine": "מכונת לחיצת חזה",
        "Pec Deck": "פק דק",
        "Barbell Incline Bench Press": "לחיצת חזה בשיפוע עם מוט",
        "Close-Grip Bench Press": "לחיצת חזה צמודה עם מוט",
        "Dumbbell Incline Press": "לחיצת חזה בשיפוע עם משקולות",
        "Lat Pulldown": "משיכות לאט",
        "Seated Row": "חתירה בישיבה",
        "T-Bar Row": "חתירת T-Bar",
        "Face Pull": "פייס פול",
        "Dumbbell Shrug": "שרג עם משקולות",
        "Barbell Curl": "כפיפת מרפקים עם מוט",
        "Hammer Curl": "כפיפת מרפקים פטיש",
        "Concentration Curl": "כפיפת מרפקים ריכוז",
        "Preacher Curl": "כפיפת מרפקים פריצ'ר",
        "Triceps Pushdown": "דחיפת טריספס",
        "Overhead Dumbbell Triceps Extension": "הארכת טריספס מעל הראש עם משקולת",
        "Dips": "דיפס",
        "Chin-Up": "מתח עם אחיזה תחתית",
        "Barbell Shoulder Press": "לחיצת כתפיים עם מוט",
        "Side Lateral Raise": "הרמה צידית",
        "Rear Delt Fly": "פלי דלתא אחורי",
        "Upright Row": "חתירה אנכית",
        "Assisted Chin up": "מתח בסיוע",
        "Barbell Clean Deadlift": "דדליפט בסגנון קלין עם מוט", // Adjusted for clarity
        "Barbell JM Press": "לחיצת JM עם מוט",
        "Bent-Over Barbell Reverse Raise (Shoulders)": "הרמה צידית אחורית כפופה עם מוט", // Specified as rear lateral raise
        "Bent-Over Two-Arm Dumbbell Row": "חתירה כפופה דו-זרועית עם משקולות",
        "Cable External Shoulder Rotation": "סיבוב חיצוני של הכתף עם כבל",
        "Cable Standing Rear Delt Horizontal Row": "חתירת דלתא אחורית אופקית עם כבל בעמידה",
        "Dumbbell Cuban Rotation": "רוטציה קובנית עם משקולת",
        "Dumbbell Scott Press": "לחיצת סקוט עם משקולת",
        "Dumbbell Seated Reverse Grip Concentration Curl": "כפיפת מרפקים ריכוז בישיבה עם אחיזה הפוכה ומשקולת",
        "Gripless shrug": "שרג ללא אחיזה",
        "High Bird Dog Plank": "פלאנק ציפור-כלב גבוה",
        "Kettlebell Kneeling One Arm Bottoms Up Press": "לחיצת חד-זרועית עם קטלבל בברכיים (תחתית למעלה)",
        "Lever Bent over Row (plate loaded)": "חתירה כפופה עם מכונה (טעינת משקולות)",
        "Lying Dumbbell One-Arm Rear Lateral Raise": "הרמה צידית אחורית חד-זרועית בשכיבה עם משקולת",
        "Lying Dumbbell Rear Lateral Raise": "הרמה צידית אחורית בשכיבה עם משקולת",
        "One-Arm Dumbbell Bent-Over Lateral Raise": "הרמה צידית חד-זרועית כפופה עם משקולת",
        "Seated Alternating Dumbbell Front Raise": "הרמה קדמית לסירוגין בישיבה עם משקולות",
        "Seated Bent-Over Lateral Raise": "הרמה צידית כפופה בישיבה",
        "Spider Crawl Push up": "שכיבות סמיכה זחילה עכבישית",
        "Standing Long Jump (female)": "קפיצה לרוחק בעמידה (נשים)",
        "Underhand Yates Row": "חתירת ייטס עם אחיזה תחתית",
        "Sumo Squat": "סקווט סומו", // Corrected from "סומו עמוק" to distinguish from Deep Squat
        "45-Degree Leg Press": "לחיצת רגליים 45 מעלות",
        "Lying Hip Thrusts with Bent Knees": "דחיפת ירך בשכיבה עם ברכיים כפופות", // Aligned with "hip thrust" terminology
        "Standing Triceps Extensions with Barbell": "הארכת טריספס בעמידה עם מוט",
        "Close-Grip Lat Pulldowns": "משיכות לאט עם אחיזה צמודה", // Corrected typo "תרווחליה"
        "Bent-Over Barbell Row": "חתירה כפופה עם מוט",
        "Trap Raises with Cable": "הרמות טרפז עם כבל", // Corrected "תרווחליה" to "כבל"
        "Dumbbell Shoulder Press – Standing": "לחיצת כתפיים עם משקולות בעמידה",
        "Front Barbell Shoulder Raises": "הרמות קדמיות לכתפיים עם מוט",
        "External Shoulder Rotation with Shoulder at 90 Degrees Abduction": "סיבוב חיצוני של הכתף ב-90 מעלות אבדוקציה",
        "Barbell Trap Raises": "הרמות טרפז עם מוט"
    };


    useEffect(() => {
        const fetchData = async () => {
            try {
                const [exerciseData, programData] = await Promise.all([
                    getExercises(),
                    getWorkoutProgram(programId),
                ]);
                setExercises(exerciseData);

                const { program, exercises: programExercises } = programData;
                setWorkoutType(program.workoutType || '');
                setExpertiseLevel(program.expertiseLevel || '');
                setTimesPerWeek(program.timesPerWeek || null);
                setRestDays(program.restDays || '');
                setGeneralNotes(program.generalNotes || []);

                const programByDay = {};
                programExercises.forEach(ex => {
                    if (!programByDay[ex.day]) programByDay[ex.day] = [];
                    const exercise = exerciseData.find(e => e._id.toString() === ex.exerciseId.toString());
                    programByDay[ex.day].push({ exercise, sets: ex.sets, reps: ex.reps, weight: ex.weight });
                });
                setCustomProgram(programByDay);

                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };
        fetchData();
    }, [programId]);

    const handleAddExercise = () => {
        if (!selectedExercise || !day || !sets || !reps) return;
        const newExercise = {
            exercise: selectedExercise,
            sets: parseInt(sets),
            reps: parseInt(reps),
            weight: weight ? parseFloat(weight) : undefined,
        };
        setCustomProgram(prev => ({
            ...prev,
            [day]: [...(prev[day] || []), newExercise],
        }));
        setOpenAddDialog(false);
        setSelectedExercise(null);
        setSets('');
        setReps('');
        setWeight('');
        setDay('');
    };

    const handleRemoveExercise = (day, index) => {
        setCustomProgram(prev => {
            const updated = { ...prev };
            updated[day] = updated[day].filter((_, i) => i !== index);
            if (updated[day].length === 0) delete updated[day];
            return updated;
        });
    };

    const handleEditSetsRepsWeight = (day, index, newSets, newReps, newWeight) => {
        setCustomProgram(prev => {
            const updated = { ...prev };
            updated[day][index] = {
                ...updated[day][index],
                sets: newSets,
                reps: newReps,
                weight: newWeight,
            };
            return updated;
        });
    };

    const generatePDF = (programData) => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            textDirection: 'rtl',
            direction: 'rtl',
        });
        doc.setR2L(true);
        doc.addFileToVFS('gevlevin.ttf', GveretLevinFont);
        doc.addFont('gevlevin.ttf', 'GveretLevin', 'normal');
        doc.setFont('GveretLevin');
        doc.setFontSize(12);

        doc.text('תוכנית אימונים', 105, 10, { align: 'center' });
        let y = 20;
        doc.text(`סוג אימון: ${programData.workoutType || 'מותאם אישית'}`, 200, y, { align: 'right' });
        y += 10;
        doc.text(`רמת ניסיון: ${programData.expertiseLevel || 'לא מוגדר'}`, 200, y, { align: 'right' });
        y += 10;
        if (programData.timesPerWeek) {
            doc.text(`מספר פעמים בשבוע: ${programData.timesPerWeek}`, 200, y, { align: 'right' });
            y += 10;
        }
        if (programData.restDays) {
            doc.text(`ימי מנוחה: ${programData.restDays}`, 200, y, { align: 'right' });
            y += 10;
        }

        const exercisesByDay = {};
        programData.exercises.forEach(ex => {
            exercisesByDay[ex.day] = exercisesByDay[ex.day] || [];
            exercisesByDay[ex.day].push(ex);
        });

        for (const [day, exercises] of Object.entries(exercisesByDay)) {
            doc.text(day, 200, y, { align: 'right' });
            y += 10;
            exercises.forEach((ex, index) => {
                const name = exerciseNameMapping[ex.exerciseName] || ex.exerciseName;
                const weightText = ex.weight ? `, משקל: ${ex.weight} ק״ג` : '';
                doc.text(`${index + 1}. ${name}: ${ex.sets} סטים של ${ex.reps} חזרות${weightText}`, 195, y, { align: 'right' });
                y += 10;
            });
            y += 5;
        }

        if (programData.generalNotes.length > 0) {
            doc.text('דגשים כללים:', 200, y, { align: 'right' });
            y += 10;
            programData.generalNotes.forEach(note => {
                doc.text(`- ${note}`, 195, y, { align: 'right' });
                y += 10;
            });
        }

        return doc.output('blob');
    };

    const handleSave = async () => {
        try {
            const programData = {
                userId,
                workoutType,
                expertiseLevel,
                timesPerWeek,
                restDays,
                generalNotes,
                exercises: Object.entries(customProgram).flatMap(([day, exercises]) =>
                    exercises.map(ex => ({
                        day,
                        exerciseId: ex.exercise._id,
                        exerciseName: ex.exercise.name,
                        sets: ex.sets,
                        reps: ex.reps,
                        weight: ex.weight,
                    }))
                ),
            };

            await updateWorkoutProgram(programId, programData);
            const pdfBlob = generatePDF(programData);
            const formData = new FormData();
            formData.append('programFile', pdfBlob, `${workoutType || 'Custom'}_Program.pdf`);
            await updateProgramByWorkoutProgramId(programId, formData);

            navigate('/program'); // Redirect back to Programs page
        } catch (error) {
            console.error('Error saving program:', error);
        }
    };

    if (loading) return <Loader />;

    return (
        <Box sx={{ padding: '20px', direction: 'rtl', margin: '0 auto', textAlign: 'center', maxWidth: '400px' }}>
            <Stack spacing={2} direction="column" alignItems="center">
                <Typography variant="h4" sx={{ color: '#8B4513', fontFamily: 'GveretLevin' }}>
                    עריכת תוכנית אימונים
                </Typography>

                <Select
                    value={workoutType}
                    onChange={(e) => setWorkoutType(e.target.value)}
                    displayEmpty
                    sx={{ direction: 'rtl', textAlign: 'right', width: '100%' }}
                >
                    <MenuItem value="">בחר סוג אימון</MenuItem>
                    <MenuItem value="powerlifting">פאוורליפטינג</MenuItem>
                    <MenuItem value="powerbuilding">פאוורבילדינג</MenuItem>
                    <MenuItem value="SBD">SBD powerlifting</MenuItem>
                    <MenuItem value="gym training classic A B C">אימון קלאסי A B C</MenuItem>
                    <MenuItem value="A B">A B</MenuItem>
                    <MenuItem value="נשים הדגשת ישבן">נשים הדגשת ישבן</MenuItem>
                </Select>

                <Select
                    value={expertiseLevel}
                    onChange={(e) => setExpertiseLevel(e.target.value)}
                    displayEmpty
                    sx={{ direction: 'rtl', textAlign: 'right', width: '100%' }}
                >
                    <MenuItem value="">בחר רמת ניסיון</MenuItem>
                    <MenuItem value="beginner">מתחיל</MenuItem>
                    <MenuItem value="intermediate">בינוני</MenuItem>
                    <MenuItem value="advanced">מתקדם</MenuItem>
                </Select>

                <TextField
                    label="מספר פעמים בשבוע"
                    type="number"
                    value={timesPerWeek || ''}
                    onChange={(e) => setTimesPerWeek(e.target.value ? parseInt(e.target.value) : null)}
                    sx={{ direction: 'rtl', textAlign: 'right', width: '100%' }}
                />
                <TextField
                    label="ימי מנוחה"
                    value={restDays}
                    onChange={(e) => setRestDays(e.target.value)}
                    sx={{ direction: 'rtl', textAlign: 'right', width: '100%' }}
                />
                <TextField
                    label="דגשים כללים"
                    multiline
                    rows={4}
                    value={generalNotes.join('\n')}
                    onChange={(e) => setGeneralNotes(e.target.value.split('\n'))}
                    sx={{ direction: 'rtl', textAlign: 'right', width: '100%' }}
                />

                <Button
                    variant="contained"
                    onClick={() => setOpenAddDialog(true)}
                    sx={{ backgroundColor: '#8B4513', color: 'white' }}
                >
                    הוסף תרגיל
                </Button>

                {Object.entries(customProgram).map(([day, exercises]) => (
                    <Stack key={day} sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ color: "#C4A484", fontFamily: 'GveretLevin', textAlign: 'right' }}>
                            {day}
                        </Typography>
                        {exercises.map((item, index) => (
                            <ProgramExerciseItem
                                key={`${item.exercise._id}-${index}`}
                                exercise={item.exercise}
                                sets={item.sets}
                                reps={item.reps}
                                weight={item.weight}
                                editable
                                onRemove={() => handleRemoveExercise(day, index)}
                                onEdit={(newSets, newReps, newWeight) => handleEditSetsRepsWeight(day, index, newSets, newReps, newWeight)}
                            />
                        ))}
                    </Stack>
                ))}

                <Button
                    onClick={handleSave}
                    sx={{ backgroundColor: "#C4A484", color: 'white', "&:hover": { backgroundColor: "#C4A484" }, width: '100%' }}
                >
                    שמירה
                </Button>
            </Stack>

            <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
                <DialogTitle sx={{ textAlign: 'right' }}>הוסף תרגיל לתוכנית</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2, direction: 'rtl' }}>
                        <Autocomplete
                            options={exercises}
                            getOptionLabel={(option) => exerciseNameMapping[option.name] || option.name}
                            value={selectedExercise}
                            onChange={(event, newValue) => setSelectedExercise(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="חפש תרגיל" sx={{ direction: 'rtl', textAlign: 'right' }} />
                            )}
                        />
                        <Autocomplete
                            options={dayOptions}
                            value={day}
                            onChange={(event, newValue) => setDay(newValue || '')}
                            renderInput={(params) => (
                                <TextField {...params} label="יום" sx={{ direction: 'rtl', textAlign: 'right' }} />
                            )}
                        />
                        <TextField
                            label="סטים"
                            type="number"
                            value={sets}
                            onChange={(e) => setSets(e.target.value)}
                            sx={{ direction: 'rtl', textAlign: 'right' }}
                        />
                        <TextField
                            label="חזרות"
                            type="number"
                            value={reps}
                            onChange={(e) => setReps(e.target.value)}
                            sx={{ direction: 'rtl', textAlign: 'right' }}
                        />
                        <TextField
                            label="משקל (ק״ג)"
                            type="number"
                            value={weight}
                            onChange={(e) => setWeight(e.target.value)}
                            sx={{ direction: 'rtl', textAlign: 'right' }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleAddExercise}
                            sx={{ backgroundColor: '#8B4513', color: 'white' }}
                        >
                            הוסף
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default EditStartWorkout;